import { createAction, createEntityAdapter, createReducer } from "@reduxjs/toolkit";
import { IRequiredInspectionsEntity } from "models/RequiredInspections.model";
import { RootState } from "store/types";
import { createDeepEqualSelector } from "store/utils";
import { selectApplicationEntityById } from "store/domain-data/application/application";

// Action & Thunks

export const loadRequiredInspections = createAction<IRequiredInspectionsEntity[]>(
  "domainData/requiredInspections/loadRequiredInspections"
);

// Entity Adapter

const requiredInspectionsAdapter = createEntityAdapter<IRequiredInspectionsEntity>({
  selectId: (entity) => entity.location,
});

// Reducer

export const defaultRequiredInspectionsState = requiredInspectionsAdapter.getInitialState();

export const requiredInspectionsReducer = createReducer<typeof defaultRequiredInspectionsState>(
  defaultRequiredInspectionsState,
  (builder) => builder.addCase(loadRequiredInspections, requiredInspectionsAdapter.upsertMany)
);

// Selectors

export const { selectAll: selectAllRequiredInspectionsEntities } = requiredInspectionsAdapter.getSelectors(
  (state: RootState) => state.domainData.requiredInspections
);

export const selectRequiredInspectionsByApplicationId = createDeepEqualSelector(
  [
    selectAllRequiredInspectionsEntities,
    (state: RootState, applicationId: number) => selectApplicationEntityById(state, applicationId),
  ],
  (entities, application) => {
    return entities.filter((entity) => entity.applicationId === application?.id);
  }
);

export const selectInspectionTypeByInspectionTypeId = createDeepEqualSelector(
  [
    selectAllRequiredInspectionsEntities,
    (_: RootState, args: { applicationId: number; inspectionTypeId: string | undefined }) => args,
  ],
  (entities, args) => {
    if (!args.inspectionTypeId) {
      return undefined;
    }
    const applicationInspections = entities.filter((entity) => entity.applicationId === args.applicationId);
    for (const appInsp of applicationInspections) {
      const applicationTypes = appInsp.inspectionTypes;
      const inspectionType = applicationTypes.find((appType) => appType.inspectionTypeId === args.inspectionTypeId);
      if (inspectionType) {
        return inspectionType;
      }
    }
  }
);
